import { makeAutoObservable, runInAction } from 'mobx';

import { electronApis } from '@/features/electron-api';
import type {
  AgentSetupResult,
  CodingAgentId,
  CodingAgentState,
  InstallStep,
  SetupActivity,
} from '@/features/electron-api/features/CodingAgentsAPI';
import { logger } from '@/lib/logger';

const SETUP_VERB: Record<SetupActivity, string> = {
  installing: 'install',
  'signing-in': 'sign in to',
};

/** A setup step under way. An install has a `step` once the main process reports the first part of it */
export type SetupProgress =
  | { activity: 'signing-in' }
  | { activity: 'installing'; step?: InstallStep };

class CodingAgentsStore {
  agents: CodingAgentState[] = [];
  /** False until the first read of the agents ends, whether it worked or not */
  loaded = false;
  busy = new Set<CodingAgentId>();
  /** The install or sign-in under way for each agent */
  activities = new Map<CodingAgentId, SetupProgress>();
  /** The terminal command for an agent whose install failed, for the user to run instead */
  installCommands = new Map<CodingAgentId, string>();
  loadError: string | null = null;
  /** Removes the install-step listener, which is there only while an install runs */
  private stopListeningForInstallSteps: (() => void) | undefined;

  constructor() {
    makeAutoObservable<this, 'stopListeningForInstallSteps'>(this, {
      stopListeningForInstallSteps: false,
    });
  }

  get anyConnected(): boolean {
    return this.agents.some((agent) => agent.connected);
  }

  async load(): Promise<void> {
    try {
      // Outside the desktop app there is no main process to answer
      const agents = (await electronApis.codingAgents.getState()) ?? [];
      runInAction(() => {
        this.agents = agents;
        this.loadError = null;
      });
    } catch (error) {
      logger.error('Could not load coding agents', { error });
      runInAction(() => {
        this.loadError = 'Could not load your coding agents. Try again.';
      });
    } finally {
      runInAction(() => {
        this.loaded = true;
      });
    }
  }

  async setConnected(id: CodingAgentId, connected: boolean): Promise<void> {
    this.busy.add(id);
    try {
      const state = await electronApis.codingAgents.setConnected(id, connected);
      this.replace(id, () => state);
    } catch (error) {
      logger.error('Could not change a coding agent', { id, error });
      this.showError(
        id,
        (label) =>
          `Could not ${connected ? 'connect' : 'disconnect'} ${label}. Try again.`
      );
    } finally {
      runInAction(() => this.busy.delete(id));
    }
  }

  async install(id: CodingAgentId): Promise<void> {
    this.listenForInstallSteps();
    try {
      await this.runSetup(id, 'installing', () =>
        electronApis.codingAgents.install(id)
      );
    } finally {
      const installing = [...this.activities.values()].some(
        (progress) => progress.activity === 'installing'
      );
      if (!installing) {
        this.stopListeningForInstallSteps?.();
        this.stopListeningForInstallSteps = undefined;
      }
    }
  }

  signIn(id: CodingAgentId): Promise<void> {
    return this.runSetup(id, 'signing-in', () =>
      electronApis.codingAgents.signIn(id)
    );
  }

  cancelSetup(id: CodingAgentId): void {
    void electronApis.codingAgents.cancelSetup(id);
  }

  async reopenSignIn(id: CodingAgentId): Promise<void> {
    const result = await electronApis.codingAgents.reopenSignIn(id);
    if (!result.success) this.showError(id, () => result.error);
  }

  /**
   * Starts showing the part of each install under way. `ElectronAPI` keeps one listener per event type, so the
   * store adds one listener for all its installs, and the last install to end removes it
   */
  private listenForInstallSteps(): void {
    if (this.stopListeningForInstallSteps) return;
    this.stopListeningForInstallSteps = electronApis.codingAgents.onInstallStep(
      ({ id, step }) => {
        runInAction(() => {
          // A step that arrives after its install ended has nothing left to show
          if (this.activities.get(id)?.activity !== 'installing') return;
          this.activities.set(id, { activity: 'installing', step });
        });
      }
    );
  }

  private async runSetup(
    id: CodingAgentId,
    activity: SetupActivity,
    call: () => Promise<AgentSetupResult>
  ): Promise<void> {
    this.activities.set(id, { activity });
    this.installCommands.delete(id);
    this.replace(id, (agent) => ({ ...agent, error: undefined }));
    try {
      const result = await call();
      if (result.success) {
        this.replace(id, () => result.state);
      } else {
        this.showError(id, () => result.error);
        const { installCommand } = result;
        if (installCommand) {
          runInAction(() => this.installCommands.set(id, installCommand));
        }
      }
    } catch (error) {
      logger.error('Coding agent setup failed', { id, activity, error });
      this.showError(
        id,
        (label) => `Could not ${SETUP_VERB[activity]} ${label}. Try again.`
      );
    } finally {
      runInAction(() => this.activities.delete(id));
    }
  }

  private replace(
    id: CodingAgentId,
    next: (agent: CodingAgentState) => CodingAgentState
  ): void {
    runInAction(() => {
      this.agents = this.agents.map((agent) =>
        agent.id === id ? next(agent) : agent
      );
    });
  }

  private showError(id: CodingAgentId, message: (label: string) => string) {
    this.replace(id, (agent) => ({ ...agent, error: message(agent.label) }));
  }
}

export const codingAgentsStore = new CodingAgentsStore();
